import { useState, useMemo, useEffect } from 'react'
import { useNavigate } from '@tanstack/react-router'
import { X, CirclePlus, CircleMinus, Users, Plane } from '@/components/icons'
import { Drawer, DrawerContent, DrawerFooter, DrawerHeader, DrawerTitle } from './ui/drawer'
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from './ui/alert-dialog'
import { Avatar, AvatarFallback } from './ui/avatar'
import { SearchInput } from './ui/search-input'
import { Button } from './ui/button'
import { DutyBadge } from './duty-status'
import { useCrewGetAll } from '../lib/hooks/crew'
import { ListSkeleton } from './ui/skeleton-presets'
import { getInitials, cn, formatCrewRole } from '../lib/utils'
import { EmptyState } from './EmptyState'

interface CrewMember {
  id: string
  name: string
  firstName?: string | null
  lastName?: string | null
  crewRole: string
  avatarColor?: string | null
  // Optional precise duty status for a date — when provided, rendered as a DutyBadge.
  dutyStatus?: string | null
}

// Unavailable crew carry the reason they can't be assigned. `flight_conflict` is shown as
// info-only with a plane icon; `no_roster` (off-duty/unrostered) can be overridden on duty.
// `otherAircraftRegistration` is set when the crew is On Duty/Standby that day on a different tail.
type UnavailableCrewMember = CrewMember & {
  reason?: 'no_roster' | 'flight_conflict'
  otherAircraftRegistration?: string | null
}

interface CrewMemberPickerDrawerBaseProps {
  isOpen: boolean
  onClose: () => void
  title?: string
  // Optional secondary line under the title, e.g. the flight route and date the
  // crew is being selected for ("OTBD → VOML · Mon, 16 Jun 2026").
  subtitle?: string
  excludeCrewIds?: string[]
  availableCrew?: CrewMember[]
  // Crew who are unavailable for the selected date — rendered below the available list.
  // Only provided by callers that have a notion of date-based availability (e.g. FlightDrawer).
  unavailableCrew?: UnavailableCrewMember[]
  // When provided, off-duty/unrostered crew (reason `no_roster`) become tappable: tapping opens an
  // "Add on-duty status?" confirmation. On confirm this records the intent (the caller writes the
  // roster on flight save) and the crew is added to the selection.
  onAssignOnDuty?: (crewId: string) => void
  isLoading?: boolean
  // Drop non-schedulable crew (includeInSchedule off) from the self-fetched list —
  // set by roster callers. Callers passing availableCrew control their own list.
  schedulableOnly?: boolean
}

interface SingleSelectProps extends CrewMemberPickerDrawerBaseProps {
  mode?: 'single'
  onSelect: (crewId: string) => void
  selectedCrewId?: string
  selectedCrewIds?: never
  onSelectMultiple?: never
}

interface MultiSelectProps extends CrewMemberPickerDrawerBaseProps {
  mode: 'multiple'
  onSelectMultiple: (crewIds: string[]) => void
  selectedCrewIds?: string[]
  onSelect?: never
  selectedCrewId?: never
}

type CrewMemberPickerDrawerProps = SingleSelectProps | MultiSelectProps

export function CrewMemberPickerDrawer(props: CrewMemberPickerDrawerProps) {
  const {
    isOpen,
    onClose,
    title,
    subtitle,
    excludeCrewIds = [],
    availableCrew,
    unavailableCrew,
    onAssignOnDuty,
    schedulableOnly = false,
    mode = 'single',
  } = props

  const navigate = useNavigate()
  const [searchQuery, setSearchQuery] = useState('')
  // Off-duty crew pending an "add on-duty" confirmation.
  const [assignTarget, setAssignTarget] = useState<UnavailableCrewMember | null>(null)
  const [selectedIds, setSelectedIds] = useState<Set<string>>(
    new Set(props.mode === 'multiple' ? props.selectedCrewIds || [] : [])
  )

  // Sync selectedIds when drawer opens
  useEffect(() => {
    if (isOpen && props.mode === 'multiple') {
      setSelectedIds(new Set(props.selectedCrewIds || []))
    }
  }, [isOpen])

  // Fetch crew members only when availableCrew is not provided
  const { data: fetchedCrewMembers = [], isPending: fetchLoading } = useCrewGetAll()
  const loading = props.isLoading ?? fetchLoading

  const crewMembers = useMemo<CrewMember[]>(() => {
    if (availableCrew) return availableCrew
    if (schedulableOnly) {
      return fetchedCrewMembers.filter((c) => c.includeInSchedule !== false)
    }
    return fetchedCrewMembers
  }, [availableCrew, fetchedCrewMembers, schedulableOnly])

  // Shared filter applied to both the available and unavailable lists:
  // drops excluded crew and narrows by the search query.
  const filterCrew = useMemo(() => {
    const excludeSet = new Set(excludeCrewIds)
    const searchLower = searchQuery.toLowerCase()
    return <T extends CrewMember>(list: T[]) =>
      list.filter((crew) => {
        if (excludeSet.has(crew.id)) return false
        if (!searchQuery) return true
        return (
          crew.name?.toLowerCase().includes(searchLower) ||
          crew.firstName?.toLowerCase().includes(searchLower) ||
          crew.lastName?.toLowerCase().includes(searchLower) ||
          crew.crewRole?.toLowerCase().includes(searchLower) ||
          formatCrewRole(crew.crewRole).toLowerCase().includes(searchLower)
        )
      })
  }, [excludeCrewIds, searchQuery])

  const filteredCrew = useMemo(
    () => filterCrew(crewMembers),
    [filterCrew, crewMembers]
  )

  const filteredUnavailable = useMemo(
    () => filterCrew(unavailableCrew ?? []),
    [filterCrew, unavailableCrew]
  )

  // One flat list: available crew (selectable) followed by unavailable crew (carry reason +
  // other-aircraft tail). Rendered uniformly so selection highlights consistently.
  const combinedCrew = useMemo(
    () => [
      ...filteredCrew.map((c) => ({ ...c, selectable: true }) as UnavailableCrewMember & { selectable: boolean }),
      ...filteredUnavailable.map((c) => ({ ...c, selectable: false })),
    ],
    [filteredCrew, filteredUnavailable]
  )

  const handleSingleSelect = (crewId: string) => {
    if (props.mode !== 'multiple' && props.onSelect) {
      props.onSelect(crewId)
      setSearchQuery('')
      onClose()
    }
  }

  const toggleCrewSelection = (crewId: string) => {
    setSelectedIds((prev) => {
      const newSet = new Set(prev)
      if (newSet.has(crewId)) {
        newSet.delete(crewId)
      } else {
        newSet.add(crewId)
      }
      return newSet
    })
  }

  const handleSelect = () => {
    if (props.mode === 'multiple' && props.onSelectMultiple) {
      props.onSelectMultiple(Array.from(selectedIds))
    }
    setSearchQuery('')
    onClose()
  }

  // Confirm overriding an off-duty crew on duty: record the intent (caller writes the roster on
  // flight save) and select them.
  const handleConfirmAssign = () => {
    if (!assignTarget || !onAssignOnDuty) return
    onAssignOnDuty(assignTarget.id)
    if (props.mode === 'multiple') {
      setSelectedIds((prev) => new Set(prev).add(assignTarget.id))
    } else if (props.onSelect) {
      props.onSelect(assignTarget.id)
      setSearchQuery('')
      onClose()
    }
    setAssignTarget(null)
  }

  const handleClose = () => {
    setSearchQuery('')
    // Reset selection to initial on close without saving
    if (props.mode === 'multiple') {
      setSelectedIds(new Set(props.selectedCrewIds || []))
    }
    onClose()
  }

  const getTitle = () => {
    if (title) return title
    return mode === 'multiple' ? 'Select Crew' : 'Crew Member'
  }

  const isSingleMode = mode === 'single'
  const selectedCrewId = isSingleMode ? (props as SingleSelectProps).selectedCrewId : undefined

  return (
    <>
    <Drawer open={isOpen} onOpenChange={(open) => !open && handleClose()}>
      <DrawerContent className="bg-surface-L0 shadow-lg max-h-[85dvh] flex flex-col">
        <DrawerHeader className="flex items-start justify-between p-4">
          <div className="flex min-w-0 flex-col gap-0.5">
            <DrawerTitle className="text-lg font-semibold text-content-primary-bold">
              {getTitle()}
            </DrawerTitle>
            {subtitle && (
              <p className="truncate text-sm text-content-primary-subtle">
                {subtitle}
              </p>
            )}
          </div>
          <Button variant="secondary" size="icon" roundness="round" className="size-8 shrink-0" onClick={handleClose}>
            <X className="w-4 h-4" />
          </Button>
        </DrawerHeader>

        {!fetchLoading && fetchedCrewMembers.length === 0 ? (
          <div className="px-4 py-6 flex flex-col flex-1">
            <EmptyState
              icon={Users}
              title="No crew members yet"
              description="Add your first crew member before selecting."
            />
          </div>
        ) : (
          <>
            {/* Content */}
            <div className="min-h-0 flex-1 overflow-hidden px-2">
              <div className="flex flex-col gap-2 p-2 h-full">
                {/* Search Input */}
                <SearchInput
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search crew"
                />

                {/* Selected count for multiple mode */}
                {!isSingleMode && selectedIds.size > 0 && (
                  <p className="text-sm text-content-primary-subtle px-1">
                    {selectedIds.size} selected
                  </p>
                )}

                {/* Crew List */}
                <div
                  className="overflow-y-auto overscroll-contain h-[300px]"
                  style={{ WebkitOverflowScrolling: 'touch' }}
                >
                  {loading ? (
                    <ListSkeleton />
                  ) : combinedCrew.length === 0 ? (
                    <div className="flex flex-col items-center justify-center py-12 px-4 gap-3">
                      <p className="text-sm text-content-primary-subtle text-center">
                        {searchQuery
                          ? 'No crew members found matching your search'
                          : availableCrew
                            ? 'No crew available for the selected date'
                            : 'No crew members available'}
                      </p>
                      {!searchQuery && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => {
                            handleClose()
                            navigate({ to: '/operations/rosters' })
                          }}
                        >
                          Go to Roster
                        </Button>
                      )}
                    </div>
                  ) : (
                    <div className="flex flex-col gap-2">
                      {combinedCrew.map((crew, index) => {
                        const initials = getInitials(crew.firstName, crew.lastName)
                        const displayName =
                          crew.firstName && crew.lastName
                            ? `${crew.firstName} ${crew.lastName}`
                            : crew.name
                        const isSelected = isSingleMode
                          ? selectedCrewId === crew.id
                          : selectedIds.has(crew.id)
                        const isConflict = crew.reason === 'flight_conflict'
                        // Off-duty/unrostered crew can be overridden on duty (admin); once selected
                        // they're removable. Available crew toggle directly.
                        const canOverride = !!onAssignOnDuty && crew.reason === 'no_roster' && !isSelected
                        const canToggle = crew.selectable || isSelected
                        const interactive = canToggle || canOverride
                        // Show On Duty optimistically for an overridden crew (roster moves on save).
                        const status = isSelected && crew.reason === 'no_roster' ? 'OnDuty' : crew.dutyStatus
                        const showTail = !!crew.otherAircraftRegistration && !isSelected
                        const onRowClick = () => {
                          if (canToggle) {
                            if (isSingleMode) handleSingleSelect(crew.id)
                            else toggleCrewSelection(crew.id)
                          } else if (canOverride) {
                            setAssignTarget(crew)
                          }
                        }
                        return (
                          <div key={crew.id} className="flex flex-col gap-2">
                            {index > 0 && <hr className="border-border-L1" />}
                            <button
                              type="button"
                              disabled={!interactive}
                              onClick={onRowClick}
                              className={cn(
                                'w-full flex items-center gap-3 min-h-9 px-3 py-2 rounded-md text-left transition-colors',
                                !interactive
                                  ? 'opacity-50 pointer-events-none'
                                  : isSelected
                                    ? 'bg-surface-L2'
                                    : 'hover:bg-surface-L2/50'
                              )}
                            >
                              <Avatar className="size-8 shrink-0"><AvatarFallback className="text-background" style={crew.avatarColor ? { backgroundColor: crew.avatarColor } : undefined}>{initials}</AvatarFallback></Avatar>
                              <div className="flex flex-1 min-w-0 flex-col items-start justify-center">
                                <span className="text-sm leading-normal tracking-[0.07px] text-content-primary-bold truncate w-full">
                                  {displayName}
                                </span>
                                <span className="text-xs leading-normal tracking-[0.18px] text-content-primary-subtle truncate w-full">
                                  {formatCrewRole(crew.crewRole)}
                                </span>
                              </div>
                              {status && (
                                <div className="flex flex-col items-end gap-0.5 shrink-0">
                                  <DutyBadge status={status} />
                                  {showTail && (
                                    <span className="text-[10px] leading-none text-content-primary-subtle">
                                      {crew.otherAircraftRegistration}
                                    </span>
                                  )}
                                </div>
                              )}
                              {isSelected ? (
                                <CircleMinus className="size-5 shrink-0 text-content-primary-subtle" />
                              ) : isConflict ? (
                                <Plane className="size-4 shrink-0 text-content-primary-subtle" />
                              ) : canOverride ? (
                                <CirclePlus className="size-5 shrink-0 text-content-primary-subtle" />
                              ) : !isSingleMode && crew.selectable ? (
                                <CirclePlus className="size-5 shrink-0 text-content-primary-subtle" />
                              ) : null}
                            </button>
                          </div>
                        )
                      })}
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Footer for multiple selection mode */}
            {!isSingleMode && (
              <DrawerFooter className="border-t flex-row">
                <Button variant="secondary" size="mini" className="flex-1" onClick={handleClose}>
                  Cancel
                </Button>
                <Button size="mini" className="flex-1" onClick={handleSelect}>
                  Select
                </Button>
              </DrawerFooter>
            )}
          </>
        )}
      </DrawerContent>
    </Drawer>

      {/* Add-on-duty confirmation for off-duty crew */}
      <AlertDialog open={assignTarget !== null} onOpenChange={(open) => !open && setAssignTarget(null)}>
        <AlertDialogContent className="bg-surface-L0">
          <AlertDialogHeader>
            <AlertDialogTitle>
              {assignTarget?.otherAircraftRegistration ? 'Reassign to This Aircraft?' : 'Add On-Duty Status?'}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {assignTarget
                ? (() => {
                    const name =
                      assignTarget.firstName && assignTarget.lastName
                        ? `${assignTarget.firstName} ${assignTarget.lastName}`
                        : assignTarget.name
                    return assignTarget.otherAircraftRegistration
                      ? `${name} is On Duty on ${assignTarget.otherAircraftRegistration}. This moves their roster to this flight's aircraft when you save.`
                      : `${name} is off duty. They'll be added to the roster as On Duty when you save this flight.`
                  })()
                : ''}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <Button variant="secondary" size="mini" onClick={() => setAssignTarget(null)}>
              Cancel
            </Button>
            <Button size="mini" onClick={handleConfirmAssign}>
              {assignTarget?.otherAircraftRegistration ? 'Reassign' : 'Add on Duty'}
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  )
}
